import { useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'

// Shared geometry
const BULLET_GEO = new THREE.SphereGeometry(0.12, 4, 4)
const BULLET_MAT = new THREE.MeshStandardMaterial({ color: '#333', metalness: 0.9, roughness: 0.2 })

const HARPOON_GEO = new THREE.ConeGeometry(0.08, 1.2, 4)
const HARPOON_MAT = new THREE.MeshStandardMaterial({ color: '#665544', metalness: 0.3, roughness: 0.7 })

const MUZZLE_GEO = new THREE.SphereGeometry(0.5, 6, 6)
const MUZZLE_MAT = new THREE.MeshBasicMaterial({ color: '#ffcc44', transparent: true, opacity: 0.6 })

export interface ProjectileState {
  id: number
  position: THREE.Vector3
  velocity: THREE.Vector3
  age: number
  type: 'bullet' | 'harpoon'
  damage: number
  maxRange: number
  distanceTraveled: number
}

interface ProjectileProps {
  projectile: ProjectileState
}

export function Projectile({ projectile }: ProjectileProps) {
  const meshRef = useRef<THREE.Mesh>(null)
  const flashRef = useRef<THREE.Mesh>(null)

  useFrame(() => {
    if (meshRef.current) {
      meshRef.current.position.copy(projectile.position)
      if (projectile.type === 'harpoon') {
        meshRef.current.lookAt(
          projectile.position.x + projectile.velocity.x,
          projectile.position.y + projectile.velocity.y,
          projectile.position.z + projectile.velocity.z,
        )
      }
    }
    if (flashRef.current) {
      const mat = flashRef.current.material as THREE.MeshBasicMaterial
      mat.opacity *= 0.85
      if (mat.opacity < 0.01) flashRef.current.visible = false
    }
  })

  const isHarpoon = projectile.type === 'harpoon'

  return (
    <>
      <mesh
        ref={meshRef}
        geometry={isHarpoon ? HARPOON_GEO : BULLET_GEO}
        material={isHarpoon ? HARPOON_MAT : BULLET_MAT}
      />
      {projectile.age < 0.2 && (
        <mesh
          ref={flashRef}
          position={projectile.position.clone()}
          geometry={MUZZLE_GEO}
          material={MUZZLE_MAT.clone()}
        />
      )}
    </>
  )
}

let _projectileId = 0
export function nextProjectileId() { return _projectileId++ }
